export interface PutToS3Options {
    onProgress?: (pct: number) => void;
    signal?: AbortSignal;
    /** Extra request headers the presigned URL's signature binds (e.g. x-amz-meta-*). */
    headers?: Record<string, string>;
}

/**
 * PUT a file/blob to a pre-signed S3 URL using XMLHttpRequest so we can report
 * real upload progress — the fetch API cannot emit upload progress in browsers.
 * Resolves on a 2xx response, rejects otherwise. Supports cancellation via an
 * optional AbortSignal. Behaviour is identical to a plain fetch PUT for callers
 * that don't pass onProgress/signal.
 */
export const putToS3WithProgress = (
    url: string,
    file: File | Blob,
    contentType: string,
    options: PutToS3Options = {},
): Promise<void> => {
    const { onProgress, signal, headers } = options;

    return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url);
        xhr.setRequestHeader("Content-Type", contentType);
        if (headers) {
            Object.entries(headers).forEach(([name, value]) => {
                if (name.toLowerCase() !== "content-type") xhr.setRequestHeader(name, value);
            });
        }

        if (onProgress) {
            xhr.upload.onprogress = (event: ProgressEvent) => {
                if (event.lengthComputable) {
                    onProgress(Math.round((event.loaded / event.total) * 100));
                }
            };
        }

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
            }
        };
        xhr.onerror = () => reject(new Error("Upload failed: network error"));
        xhr.onabort = () => reject(new DOMException("Upload aborted", "AbortError"));

        if (signal) {
            if (signal.aborted) {
                xhr.abort();
                return;
            }
            signal.addEventListener("abort", () => xhr.abort(), { once: true });
        }

        xhr.send(file);
    });
};
